import { useCallback, useEffect, useMemo, useState } from "react";
import { MoreVertical, Pencil, Plus, RefreshCw, Search } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { ErrorNotice, Loading } from "../../components/Feedback";
import { useSession } from "../../state/SessionContext";
import {
  getAssignedMaterials,
  getSpecificationDetail,
  getSpecifications,
  setMaterialActive,
  setSpecificationActive,
} from "./api";
import { ConfirmDialog, MaterialDialog, SpecificationDialog } from "./Dialogs";
import { ActivityMaterialDeliverablesPanel } from "./ActivityMaterialDeliverablesPanel";
import { SpecificationDetailView } from "./SpecificationDetailView";
import type { ContractActivityMaterial, Specification, SpecificationDetail } from "./types";
import { qualityError } from "./utils";

type ConfirmState = { type: "material" | "specification"; id: string; active: boolean };

export function QualitySpecificationsPage() {
  const { context } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const materialId = searchParams.get("material") ?? "";
  const specificationId = searchParams.get("spec") ?? "";

  const [materials, setMaterials] = useState<ContractActivityMaterial[]>([]);
  const [specifications, setSpecifications] = useState<Specification[]>([]);
  const [detail, setDetail] = useState<SpecificationDetail | null>(null);
  const [detailError, setDetailError] = useState("");
  const [loadingMaterials, setLoadingMaterials] = useState(true);
  const [loadingSpecifications, setLoadingSpecifications] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState<{ message: string; error?: boolean } | null>(null);
  const [search, setSearch] = useState("");
  const [classFilter, setClassFilter] = useState("todos");
  const [statusFilter, setStatusFilter] = useState("activos");
  const [materialDialog, setMaterialDialog] = useState<ContractActivityMaterial | "new" | null>(null);
  const [specificationDialog, setSpecificationDialog] = useState<Specification | "new" | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [busy, setBusy] = useState(false);
  const [deliverablesRefreshToken, setDeliverablesRefreshToken] = useState(0);

  const selectedMaterial = materials.find((item) => item.id === materialId) ?? null;
  const selectedSpecification = specifications.find((item) => item.id === specificationId) ?? null;
  // `puede_editar` is authoritative only when the backend explicitly returns false.
  // Missing frontend permission metadata must not hide actions that the RPC can authorize.
  const canManageSpecification = selectedSpecification?.puede_editar !== false;

  const setSelection = useCallback((nextMaterialId?: string, nextSpecificationId?: string) => {
    const next = new URLSearchParams();
    if (nextMaterialId) next.set("material", nextMaterialId);
    if (nextSpecificationId) next.set("spec", nextSpecificationId);
    setSearchParams(next, { replace: true });
  }, [setSearchParams]);

  const notify = useCallback((message: string, isError = false) => {
    setToast({ message, error: isError });
    window.setTimeout(() => setToast(null), 4000);
  }, []);

  const loadMaterials = useCallback(async (preferredMaterialId?: string, preserveSpecificationId?: string) => {
    if (!context) return;
    setLoadingMaterials(true);
    setError("");
    try {
      const data = await getAssignedMaterials(context);
      setMaterials(data);
      const candidate = preferredMaterialId || materialId;
      const validMaterialId = data.some((item) => item.id === candidate) ? candidate : data[0]?.id;
      setSelection(validMaterialId, validMaterialId === materialId ? preserveSpecificationId : undefined);
    } catch (caught) {
      setError(qualityError(caught));
      setMaterials([]);
      setSpecifications([]);
      setDetail(null);
      setSelection();
    } finally {
      setLoadingMaterials(false);
    }
  }, [context, materialId, setSelection]);

  const loadSpecifications = useCallback(async (preferredSpecificationId?: string) => {
    if (!context || !materialId) {
      setSpecifications([]);
      setDetail(null);
      return;
    }
    setLoadingSpecifications(true);
    try {
      const data = await getSpecifications(context, materialId);
      setSpecifications(data);
      const candidate = preferredSpecificationId || specificationId;
      const matchingSpecification = data.find((item) => item.id === candidate || item.especificacion_id === candidate);
      const validSpecificationId = matchingSpecification?.id || data[0]?.id;
      setSelection(materialId, validSpecificationId);
    } catch (caught) {
      notify(qualityError(caught), true);
      setSpecifications([]);
      setDetail(null);
      setSelection(materialId);
    } finally {
      setLoadingSpecifications(false);
    }
  }, [context, materialId, specificationId, notify, setSelection]);

  const loadDetail = useCallback(async () => {
    if (!context || !specificationId) {
      setDetail(null);
      setDetailError("");
      return;
    }
    setLoadingDetail(true);
    setDetailError("");
    try {
      const result = await getSpecificationDetail(context, specificationId);
      setDetail({
        ...result,
        especificacion: {
          ...result.especificacion,
          ...selectedSpecification,
          id: specificationId,
          especificacion_proyecto_id: specificationId,
          especificacion_id: specificationId,
        },
      });
    } catch (caught) {
      const message = qualityError(caught);
      setDetailError(message);
      setDetail(null);
      notify(message, true);
    } finally {
      setLoadingDetail(false);
    }
  }, [context, specificationId, selectedSpecification, notify]);

  useEffect(() => {
    setMaterials([]);
    setSpecifications([]);
    setDetail(null);
    setDetailError("");
    setSelection();
    void loadMaterials();
  }, [context?.proyecto_id, context?.contrato_id]);

  useEffect(() => {
    setSpecifications([]);
    setDetail(null);
    setDetailError("");
    if (materialId) void loadSpecifications();
  }, [materialId]);

  useEffect(() => {
    setDetail(null);
    setDetailError("");
    if (specificationId && selectedSpecification) void loadDetail();
  }, [specificationId, selectedSpecification?.id]);

  const filteredMaterials = useMemo(() => materials.filter((item) => {
    const text = `${item.nombre} ${item.nombre_contractual ?? ""} ${item.clave ?? ""}`.toLowerCase();
    return text.includes(search.toLowerCase())
      && (classFilter === "todos" || item.clase === classFilter)
      && (statusFilter === "todos" || item.is_active === (statusFilter === "activos"));
  }), [materials, search, classFilter, statusFilter]);

  async function refreshAll() {
    await loadMaterials(materialId, specificationId);
    if (materialId) await loadSpecifications(specificationId);
    if (specificationId) await loadDetail();
    notify("Información actualizada.");
  }

  async function changeActive() {
    if (!context || !confirm) return;
    setBusy(true);
    try {
      if (confirm.type === "material") {
        await setMaterialActive(context, confirm.id, confirm.active);
        await loadMaterials(confirm.id, specificationId);
      } else {
        await setSpecificationActive(context, confirm.id, confirm.active);
        await loadSpecifications(confirm.id);
        await loadDetail();
        setDeliverablesRefreshToken((current) => current + 1);
      }
      notify(confirm.active ? "Elemento reactivado." : "Elemento desactivado.");
      setConfirm(null);
    } catch (caught) {
      notify(qualityError(caught), true);
    } finally {
      setBusy(false);
    }
  }

  function selectMaterial(id: string) {
    setSpecifications([]);
    setDetail(null);
    setDetailError("");
    setSelection(id);
  }

  function selectSpecification(id: string) {
    setDetail(null);
    setDetailError("");
    setSelection(materialId, id);
  }

  return <>
    <div className="page-heading quality-heading">
      <div>
        <span className="eyebrow">CONFIGURACIÓN DE CALIDAD</span>
        <h1>Materiales y especificaciones</h1>
        <p>Administra la estructura contractual de entregables, pruebas y criterios de cumplimiento.</p>
      </div>
      <button className="button secondary" disabled={loadingMaterials || loadingSpecifications || loadingDetail} onClick={() => void refreshAll()}>
        <RefreshCw size={16} /> Actualizar
      </button>
    </div>
    {error && <ErrorNotice message={error} />}

    <div className="quality-master-detail">
      <aside className="materials-pane panel">
        <header>
          <div><h2>Materiales / actividades</h2><span>{filteredMaterials.length === materials.length ? `${materials.length} asignados` : `${filteredMaterials.length} de ${materials.length}`}</span></div>
          <button className="button primary small quality-primary-cta" onClick={() => setMaterialDialog("new")}>
            <Plus size={15} /> Material / actividad
          </button>
        </header>
        <div className="material-filters">
          <div className="search-box"><Search size={16} /><input placeholder="Buscar…" value={search} onChange={(event) => setSearch(event.target.value)} /></div>
          <div className="two-filters">
            <select aria-label="Filtrar clase" value={classFilter} onChange={(event) => setClassFilter(event.target.value)}><option value="todos">Todos</option><option value="material">Materiales</option><option value="actividad">Actividades</option></select>
            <select aria-label="Filtrar estado" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><option value="activos">Activos</option><option value="inactivos">Inactivos</option><option value="todos">Todos</option></select>
          </div>
        </div>
        {loadingMaterials ? <Loading /> : !filteredMaterials.length ? <div className="quality-placeholder compact"><strong>No hay materiales o actividades asignados.</strong><button className="button primary small" onClick={() => setMaterialDialog("new")}><Plus size={15} /> Material / actividad</button></div> : <div className="material-list">{filteredMaterials.map((item) => <article className={`${item.id === materialId ? "selected" : ""} ${!item.is_active ? "muted-item" : ""}`} key={item.id}>
          <button className="material-select" onClick={() => selectMaterial(item.id)}>
            <strong>{item.nombre_contractual || item.nombre}</strong>
            <small>{item.clave ? `${item.clave} · ` : ""}{item.clase === "actividad" ? "Actividad" : "Material"}{item.tipo ? ` · ${item.tipo}` : ""}</small>
            <span><span className={`badge ${item.origen}`}>{item.origen === "generico" ? "Genérico" : "Contractual"}</span><span className={`status-dot ${item.is_active ? "active" : ""}`}>{item.is_active ? "Activo" : "Inactivo"}</span></span>
          </button>
          <div className="item-menu">
            <button className="icon-button" aria-label={`Acciones de ${item.nombre}`}><MoreVertical size={17} /></button>
            <div className="item-menu-popover">
              {item.origen === "contractual" && item.puede_editar !== false && <button onClick={() => setMaterialDialog(item)}><Pencil size={14} /> Editar</button>}
              {item.puede_editar !== false && <button onClick={() => setConfirm({ type: "material", id: item.id, active: !item.is_active })}>{item.is_active ? "Desactivar" : "Reactivar"}</button>}
            </div>
          </div>
        </article>)}</div>}
      </aside>

      <div className="quality-right-pane">
        <ActivityMaterialDeliverablesPanel
          context={context!}
          material={selectedMaterial}
          canEdit={selectedMaterial?.puede_editar !== false && Boolean(selectedMaterial?.is_active)}
          refreshToken={deliverablesRefreshToken}
          notify={notify}
        />

        <section className="panel spec-list-panel">
          <header>
            <div><h2>Especificaciones</h2><span>{selectedMaterial ? `Para ${selectedMaterial.nombre_contractual || selectedMaterial.nombre}` : "Selecciona un material"}</span></div>
            {selectedMaterial && <button className="button primary small quality-primary-cta" disabled={!selectedMaterial.is_active} onClick={() => setSpecificationDialog("new")}><Plus size={15} /> Especificación</button>}
          </header>
          {loadingSpecifications ? <Loading /> : !selectedMaterial ? <div className="quality-placeholder compact"><strong>Selecciona un material o actividad.</strong></div> : !specifications.length ? <div className="quality-placeholder compact"><strong>Este material no tiene especificaciones asignadas.</strong><button className="button primary small" onClick={() => setSpecificationDialog("new")}><Plus size={15} /> Especificación</button></div> : <div className="spec-list">{specifications.map((item) => <article className={`${item.id === specificationId ? "selected" : ""} ${!item.is_active ? "muted-item" : ""}`} key={item.id}>
            <button onClick={() => selectSpecification(item.id)}>
              <span className="spec-code">{item.codigo || "S/C"}</span>
              <span><strong>{item.titulo}</strong><small>{[item.organizacion, item.version].filter(Boolean).join(" · ") || "Sin datos editoriales"}</small></span>
              <span className={`badge ${item.origen}`}>{item.origen === "generico" ? "Heredada" : "Específica"}</span>
              {!item.is_active && <span className="badge unavailable">Inactiva</span>}
            </button>
            <div className="item-menu">
              <button className="icon-button" aria-label={`Acciones de ${item.codigo}`}><MoreVertical size={17} /></button>
              <div className="item-menu-popover">
                {item.origen === "contractual" && item.puede_editar !== false && <button onClick={() => setSpecificationDialog(item)}><Pencil size={14} /> Editar</button>}
                {item.puede_editar !== false && <button onClick={() => setConfirm({ type: "specification", id: item.id, active: !item.is_active })}>{item.is_active ? "Desactivar" : "Reactivar"}</button>}
              </div>
            </div>
          </article>)}</div>}
        </section>

        <div className="quality-detail-row">
          <SpecificationDetailView
            context={context!}
            detail={detail}
            selected={selectedSpecification}
            loading={loadingDetail}
            error={detailError}
            canEdit={canManageSpecification && Boolean(selectedSpecification?.is_active)}
            onRefresh={async () => {
              await loadDetail();
              setDeliverablesRefreshToken((current) => current + 1);
            }}
            onEdit={() => selectedSpecification && setSpecificationDialog(selectedSpecification)}
            onToggle={() => selectedSpecification && setConfirm({ type: "specification", id: selectedSpecification.id, active: !selectedSpecification.is_active })}
            notify={notify}
          />
        </div>
      </div>
    </div>

    {materialDialog && context && <MaterialDialog context={context} edit={materialDialog === "new" ? undefined : materialDialog} onClose={() => setMaterialDialog(null)} onSaved={async (id) => { setMaterialDialog(null); await loadMaterials(id || materialId, specificationId); notify("Material guardado correctamente."); }} />}
    {specificationDialog && context && selectedMaterial && <SpecificationDialog context={context} materialId={selectedMaterial.id} edit={specificationDialog === "new" ? undefined : specificationDialog} onClose={() => setSpecificationDialog(null)} onSaved={async (id) => { setSpecificationDialog(null); await loadSpecifications(id || specificationId); setDeliverablesRefreshToken((current) => current + 1); notify("Especificación guardada correctamente."); }} />}
    {confirm && <ConfirmDialog title={confirm.active ? "Reactivar elemento" : "Desactivar elemento"} message={confirm.active ? "El elemento volverá a estar disponible en el contrato." : "El elemento se conservará para trazabilidad, pero dejará de estar activo."} busy={busy} onClose={() => setConfirm(null)} onConfirm={() => void changeActive()} />}
    {toast && <div className={`quality-toast ${toast.error ? "error" : ""}`}>{toast.message}</div>}
  </>;
}
